import { deepMerge } from '../../utils';

import {
    TypographyBaseFontSize,
    TypographyBaseLineHeight,
    TypographyFamilyBodyFontFamily,
    TypographyFamilyBodyFontWeightBold,
    TypographyFamilyBodyFontWeightDefault,
    TypographyFamilyBodyFontWeightSemiBold,
    TypographyFamilyCaptionFontFamily,
    TypographyFamilyCaptionFontWeightBold,
    TypographyFamilyCaptionFontWeightDefault,
    TypographyFamilyCaptionFontWeightSemiBold,
    TypographyFamilyHeadingFontFamily,
    TypographyFamilyHeadingFontWeightDefault,
    TypographyFamilyHeroFontFamily,
    TypographyFamilyHeroFontWeightDefault,
    TypographyFamilyPriceFontFamily,
    TypographyFamilyPriceFontWeightDefault,
    TypographyLineHeightL,
    TypographyLineHeightM,
    TypographyLineHeightS,
    TypographyLineHeightXs,
} from '../../design-tokens/honeycomb-tokens';
import {
    HoneycombTypographyOverrides,
    HoneycombTypography,
    TypographyFamilyProps,
    TypographyResponsiveSizeProps,
} from '../types/HoneycombTypography';

const defaultLineHeights = {
    xs: TypographyLineHeightXs,
    s: TypographyLineHeightS,
    m: TypographyLineHeightM,
    l: TypographyLineHeightL,
};

// Families
const defaultFamilyBody: TypographyFamilyProps = {
    fontFamily: TypographyFamilyBodyFontFamily,
    fontWeight: {
        default: TypographyFamilyBodyFontWeightDefault,
        regular: TypographyFamilyBodyFontWeightDefault,
        semiBold: TypographyFamilyBodyFontWeightSemiBold,
        bold: TypographyFamilyBodyFontWeightBold,
    },
    textTransform: 'none',
};

const defaultFamilyHeading: TypographyFamilyProps = {
    fontFamily: TypographyFamilyHeadingFontFamily,
    fontWeight: {
        default: TypographyFamilyHeadingFontWeightDefault,
    },
    textTransform: 'none',
};

const defaultFamilyHero: TypographyFamilyProps = {
    fontFamily: TypographyFamilyHeroFontFamily,
    fontWeight: {
        default: TypographyFamilyHeroFontWeightDefault,
    },
    textTransform: 'none',
};

const defaultFamilyPrice: TypographyFamilyProps = {
    fontFamily: TypographyFamilyPriceFontFamily,
    fontWeight: {
        default: TypographyFamilyPriceFontWeightDefault,
    },
    textTransform: 'none',
};

const defaultFamilyCaption: TypographyFamilyProps = {
    fontFamily: TypographyFamilyCaptionFontFamily,
    fontWeight: {
        default: TypographyFamilyCaptionFontWeightDefault,
        regular: TypographyFamilyCaptionFontWeightDefault,
        semiBold: TypographyFamilyCaptionFontWeightSemiBold,
        bold: TypographyFamilyCaptionFontWeightBold,
    },
    textTransform: 'none',
};

// Body
const defaultVariantBodyXs: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '0.75rem', // 12px
        lineHeight: '1.125rem', // 18px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '0.75rem', // 12px
        lineHeight: '1.125rem', // 18px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '0.75rem', // 12px
        lineHeight: '1.125rem', // 18px
        letterSpacing: 'normal',
    },
};

const defaultVariantBodyS: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        letterSpacing: 'normal',
    },
};

const defaultVariantBodyM: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
};

const defaultVariantBodyL: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.75rem', // 28px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.75rem', // 28px
        letterSpacing: 'normal',
    },
};

const defaultVariantBodyXl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.75rem', // 28px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.25rem', // 20px
        lineHeight: '1.75rem', // 28px
        letterSpacing: 'normal',
    },
};

const defaultVariantBody2xl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.75rem', // 28px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.375rem', // 22px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
};

const defaultVariantBody3xl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.75rem', // 28px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.5rem', // 24px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
};

// Heading
const defaultVariantHeading2Xs: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.125rem', // 18px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.125rem', // 18px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '0.875rem', // 14px
        lineHeight: '1.125rem', // 18px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeadingXs: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1rem', // 16px
        lineHeight: '1.25rem', // 20px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1rem', // 16px
        lineHeight: '1.25rem', // 20px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1rem', // 16px
        lineHeight: '1.25rem', // 20px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeadingS: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.125rem', // 18px
        lineHeight: '1.5rem', // 24px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeadingM: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.5rem', // 24px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.5rem', // 24px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.5rem', // 24px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeadingL: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.75rem', // 28px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.75rem', // 28px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.75rem', // 28px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeadingXl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.75rem', // 28px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '2rem', // 32px
        lineHeight: '2rem', // 32px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '2rem', // 32px
        lineHeight: '2.25rem', // 36px
        letterSpacing: 'normal',
    },
};

// Hero
const defaultVariantHeroXs: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.125rem', // 18px
        lineHeight: 1, // 18px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.125rem', // 18px
        lineHeight: 1, // 18px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.125rem', // 18px
        lineHeight: 1, // 18px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeroS: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.5rem', // 24px
        lineHeight: 1, // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.5rem', // 24px
        lineHeight: 1, // 24px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.5rem', // 24px
        lineHeight: 1, // 24px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeroM: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.75rem', // 28px
        lineHeight: 1, // 28px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.75rem', // 28px
        lineHeight: 1, // 28px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.75rem', // 28px
        lineHeight: 1, // 28px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeroL: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
};

const defaultVariantHeroXl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '2.25rem', // 36px
        lineHeight: 1, // 36px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '2.25rem', // 36px
        lineHeight: 1, // 36px
        letterSpacing: 'normal',
    },
};

const defaultVariantHero2xl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '2.25rem', // 36px
        lineHeight: 1, // 36px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '2.5rem', // 40px
        lineHeight: 1, // 40px
        letterSpacing: 'normal',
    },
};

const defaultVariantHero3xl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '2.5rem', // 40px
        lineHeight: 1, // 40px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '3rem', // 48px
        lineHeight: 1, // 48px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '3.5rem', // 56px
        lineHeight: 1, // 56px
        letterSpacing: 'normal',
    },
};

// Price
const defaultVariantPrice2xs: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1rem', // 16px
        lineHeight: 1, // 16px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1rem', // 16px
        lineHeight: 1, // 16px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1rem', // 16px
        lineHeight: 1, // 16px
        letterSpacing: 'normal',
    },
};

const defaultVariantPriceXs: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.125rem', // 18px
        lineHeight: 1, // 18px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.125rem', // 18px
        lineHeight: 1, // 18px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.125rem', // 18px
        lineHeight: 1, // 18px
        letterSpacing: 'normal',
    },
};

const defaultVariantPriceS: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.25rem', // 20px
        lineHeight: 1, // 20px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.25rem', // 20px
        lineHeight: 1, // 20px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.25rem', // 20px
        lineHeight: 1, // 20px
        letterSpacing: 'normal',
    },
};

const defaultVariantPriceM: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.5rem', // 24px
        lineHeight: 1, // 24px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '1.5rem', // 24px
        lineHeight: 1, // 24px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '1.5rem', // 24px
        lineHeight: 1, // 24px
        letterSpacing: 'normal',
    },
};

const defaultVariantPriceL: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '1.75rem', // 28px
        lineHeight: 1, // 28px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
};

const defaultVariantPriceXl: TypographyResponsiveSizeProps = {
    xs: {
        fontSize: '2rem', // 32px
        lineHeight: 1, // 32px
        letterSpacing: 'normal',
    },
    m: {
        fontSize: '2.25rem', // 36px
        lineHeight: 1, // 36px
        letterSpacing: 'normal',
    },
    l: {
        fontSize: '2.5rem', // 40px
        lineHeight: 1, // 40px
        letterSpacing: 'normal',
    },
};

const defaultOverlayTextShadow = '0 2px 4px rgba(0, 0, 0, 0.6)';

const DEFAULT_TYPOGRAPHY: HoneycombTypography = {
    baseFontSize: TypographyBaseFontSize,
    baseLineHeight: TypographyBaseLineHeight,
    lineHeight: defaultLineHeights,
    overlayTextShadow: defaultOverlayTextShadow,
    family: {
        body: defaultFamilyBody,
        heading: defaultFamilyHeading,
        hero: defaultFamilyHero,
        price: defaultFamilyPrice,
        caption: defaultFamilyCaption,
    },
    variant: {
        body: {
            xs: defaultVariantBodyXs,
            s: defaultVariantBodyS,
            m: defaultVariantBodyM,
            l: defaultVariantBodyL,
            xl: defaultVariantBodyXl,
            '2xl': defaultVariantBody2xl,
            '3xl': defaultVariantBody3xl,
        },
        heading: {
            '2xs': defaultVariantHeading2Xs,
            xs: defaultVariantHeadingXs,
            s: defaultVariantHeadingS,
            m: defaultVariantHeadingM,
            l: defaultVariantHeadingL,
            xl: defaultVariantHeadingXl,
        },
        hero: {
            xs: defaultVariantHeroXs,
            s: defaultVariantHeroS,
            m: defaultVariantHeroM,
            l: defaultVariantHeroL,
            xl: defaultVariantHeroXl,
            '2xl': defaultVariantHero2xl,
            '3xl': defaultVariantHero3xl,
        },
        price: {
            '2xs': defaultVariantPrice2xs,
            xs: defaultVariantPriceXs,
            s: defaultVariantPriceS,
            m: defaultVariantPriceM,
            l: defaultVariantPriceL,
            xl: defaultVariantPriceXl,
        },
    },
};

export function createTypography(overrides?: HoneycombTypographyOverrides): HoneycombTypography {
    return deepMerge(DEFAULT_TYPOGRAPHY, overrides);
}
